import { Inject, Injectable } from '@angular/core';
import { IRequestCallback, RequestService } from '@df/api/request/request.service';
import { App } from '@df/core/app';
import { Client } from '@df/core/service/client';
import { CookiesService } from '@df/core/service/cookies';
import { Status } from '@df/core/status';
import { TimeoutService } from '@df/ng/timeout.service';
import { CustomerModel } from '@df/session/api/customer.model';
import compact from 'lodash-es/compact';
import { PIO_OPTIONS } from '../config/pio-options';
import { IPioOptions, IPioProductId } from './interfaces';
import { PrivacyService } from '@df/module-privacy/privacy.service';
import { NDfPrivacy } from '@df/module-privacy/df-module-privacy.interfaces';

@Injectable({
    providedIn: 'root'
})
export class PioService {
    /**
     * User ID retrieve status
     * @type {Status}
     */
    private uidStatus = new Status();

    /**
     * Cookie name (including cookieSeries)
     * @type {string}
     */
    get cookieName(): string {
        return this.pioConfig.user.cookieSeries ? 'pio.' + this.pioConfig.user.cookieSeries : 'pio';
    }

    /**
     * User id request promise
     */
    private uidRequest?: Promise<number>;

    /**
     * Client service
     */
    constructor(
        private app: App,
        private customer: CustomerModel,
        private client: Client,
        private requestService: RequestService,
        private privacyService: PrivacyService,
        private cookiesService: CookiesService,
        private timeoutSerice: TimeoutService,
        @Inject(PIO_OPTIONS) readonly pioConfig: IPioOptions
    ) {}

    ////////////////////////////////////////////////////////
    ///
    /// GENERAL
    ///
    ////////////////////////////////////////////////////////

    /**
     * Checks if pio FE is globally enabled
     * @return {boolean} [description]
     */
    get enabled(): boolean {
        if (this.client.isBot) {
            return false;
        }

        return this.pioConfig.enabled;
    }

    get trackingEnabled(): boolean {
        return this.enabled && this.privacyService.isAllowed(NDfPrivacy.AREA.personalisation);
    }

    /**
     * method for send request without forcing re run on same data
     */
    sendUidRequest = this.timeoutSerice.debounce(() => {
        this._sendUidRequest();
    }, 100);

    ////////////////////////////////////////////////////////
    ///
    /// USER ID MANIPULATION
    ///
    ////////////////////////////////////////////////////////

    /**
     * Provides user id
     */
    getUserId(callback: (userId: number | undefined) => any | void) {
        if (!this.trackingEnabled) {
            callback(undefined);
        } else {
            // try freshest possible (from cookie)
            const userId = this.getCookieUserId();
            if (userId) {
                callback(userId);
            } else {
                this._sendUidRequest(false, false, () => {
                    callback(this.getCookieUserId());
                });
            }
        }
    }

    /**
     * Retrieves user id from cookie
     * @return {number}
     */

    getCookieUserId(): number | undefined {
        const pioCookie = this.cookiesService.get(this.cookieName);
        return pioCookie ? parseInt(pioCookie, 10) : undefined;
    }

    /**
     * Sends
     * @param {[type]} force [description]
     */
    private _sendUidRequest(force: boolean = false, reset: boolean = false, onSuccess?: IRequestCallback) {
        if (!this.trackingEnabled) {
            return undefined;
        }
        if (!Array.isArray(onSuccess)) {
            onSuccess = compact([onSuccess]);
        }

        const promise = this.requestService.send<{ uid: number }>(
            this.uidStatus,
            {
                path: 'pio/uid',
                data: {
                    uid: reset ? undefined : this.getCookieUserId(),
                    email: this.customer.getSeenEmail(),
                    customerId: this.customer.data.id
                }
            },
            [result => this.cookiesService.set(this.cookieName, '' + result!.uid), ...onSuccess],
            undefined,
            {
                api: 'node'
            }
        );
        if (!promise && onSuccess) {
            // sand once may may return nothing
            onSuccess.forEach(cb => cb({ uid: this.getCookieUserId() }));
        }

        return promise;
    }

    resetUid() {
        this._sendUidRequest(false, true);
    }

    /**
     * Converts product id to PIO item id
     */
    toItemId(productId: number | string): string {
        if (this.app.data?.['pio_config']?.multisiteId) {
            return [this.app.multisite.storeId, productId].join('_');
        }
        return '' + productId;
    }

    /**
     * Converts PIO item id to product id
     */
    toProductId(itemId: string): IPioProductId {
        if (this.app.data?.['pio_config']?.multisiteId) {
            const parts = itemId.split('_');
            if (parts.length === 2) {
                return { store: parseInt(parts[0], 10), id: parseInt(parts[1], 10) };
            }
        }
        return { store: this.app.multisite.storeId, id: parseInt(itemId, 10) };
    }
}
